import { Link, useParams } from "react-router-dom";
import useSeo from "@/lib/useSeo";
import { useShop } from "@/context/ShopContext";

const PAGES = {
  shipping: { title: "Shipping Information", sections: [
    ["UK delivery", "delivery_details"],
    ["Processing", "processing_time"],
    ["International", "International shipping is coming soon. Please contact us for bespoke delivery requests."],
  ] },
  returns: { title: "Returns & Refunds", sections: [
    ["Our promise", "We want you to love every piece. If something isn't quite right, you may return unworn items with original tags within 14 days of delivery."],
    ["How to return", "Email us with your order number and the item(s) you'd like to return. We'll send you instructions and a returns address."],
    ["Refunds", "Once received and inspected, refunds are issued to your original payment method within 5–10 working days. Original delivery charges are non-refundable."],
    ["Exchanges", "For a different size, please return the original item and place a new order to secure your size."],
  ] },
  privacy: { title: "Privacy Policy", sections: [
    ["Information we collect", "We collect the details you provide when placing an order or contacting us — such as your name, email, phone number and delivery address."],
    ["How we use it", "Your information is used solely to process orders, deliver your items and respond to enquiries. We never sell your data."],
    ["Payments", "Payments are processed securely by our payment provider. We never see or store your full card details."],
    ["Your rights", "Under UK GDPR you may request access to, correction of, or deletion of your personal data at any time by contacting us."],
  ] },
  terms: { title: "Terms & Conditions", sections: [
    ["General", "By using this website and placing an order you agree to these terms. Maison Madison reserves the right to update them at any time."],
    ["Orders & pricing", "All prices are shown in GBP and include VAT where applicable. Orders are subject to availability and acceptance."],
    ["Product information", "We make every effort to display colours and details accurately; slight variations may occur due to screen settings and the handcrafted nature of our pieces."],
    ["Governing law", "These terms are governed by the laws of England and Wales."],
  ] },
};

export default function Policy() {
  const { page } = useParams();
  const data = PAGES[page] || PAGES.shipping;
  const { settings } = useShop();
  useSeo(data.title);
  return (
    <div data-testid={`policy-page-${page}`} className="px-5 sm:px-10 lg:px-16 py-16 sm:py-24 grid lg:grid-cols-12 gap-10">
      <aside className="lg:col-span-3 space-y-3">
        <p className="eyebrow text-[var(--mm-red)] mb-5">Client care</p>
        {Object.entries(PAGES).map(([k, v]) => (
          <Link key={k} to={`/policies/${k}`} data-testid={`policy-nav-${k}`} className={`block text-sm ${k === page ? "text-[var(--mm-red)] italic font-serif-mm text-xl" : "hover:text-[var(--mm-red)]"}`}>{v.title}</Link>
        ))}
      </aside>
      <div className="lg:col-span-7 lg:col-start-5">
        <h1 data-testid="policy-title" className="font-serif-mm text-5xl sm:text-6xl leading-none mb-14">{data.title}</h1>
        {data.sections.map(([h, t]) => [h, ["delivery_details", "processing_time"].includes(t) ? settings?.[t] : t]).filter(([, t]) => t).map(([h, t]) => (
          <div key={h} className="py-7 border-t border-[var(--mm-line)] grid sm:grid-cols-3 gap-4">
            <h2 className="eyebrow pt-1">{h}</h2>
            <p className="sm:col-span-2 text-sm sm:text-base leading-relaxed text-[var(--mm-muted)] whitespace-pre-line">{t}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
